import {Injectable, isDevMode} from '@angular/core';
import {AngularFirestore} from "@angular/fire/compat/firestore";
import {Reciept} from "./DonationModel";
import {Observable} from "rxjs";
import {map} from "rxjs/operators";
import {CustomPayment} from "../../modules/donations/customPayments/custom-payments-table/custom-payments-table.component";
import {environment} from "../../../environments/environment";
import {collection, getCountFromServer, getFirestore, query} from "firebase/firestore";

@Injectable({
    providedIn: 'root'
})
export class DonationService {
    private readonly customPaymentsCollection: string;
    private readonly env = isDevMode() ? environment.testMode : environment.productionMode;

    constructor(private mFirestore: AngularFirestore) {
        this.customPaymentsCollection = this.env.customPayments;
    }

    getDonations() {
        return this.mFirestore.collection(this.env.donations).valueChanges({idField: 'id'})
    }

    getDonationsWithId() {
        return this.mFirestore.collection<Reciept>(this.env.donations, ref => ref.orderBy('createdAt', 'desc')).snapshotChanges()
    }

    getFailedDonationsWithId() {
        return this.mFirestore.collection<Reciept>(this.env.failedDonations).snapshotChanges()
    }

    getFilteredDonationsWithId(timestamp: number) {

        return this.mFirestore.collection<Reciept>(this.env.donations, ref => ref.where('createdAt', '>=', timestamp)).snapshotChanges()
    }

    deleteDoc(doc_id: any) {
        return this.mFirestore.collection(this.env.donations).doc(doc_id).delete()
    }

    getByRange(startDate: Date | null | undefined, endDate: Date | null | undefined) {
        return this.mFirestore.collection<Reciept>(this.env.donations, ref => ref.where('date', '>=', startDate)
            .where('date', '>=', startDate)
        ).valueChanges({idField: 'id'})

    }

// Donation Dashboard related methods
  getAttemptedDonationsCount(): Observable<number> {
    return this.mFirestore.collection(this.env.attemptedDonations).valueChanges().pipe(
      map(docs => docs.length) // just count documents
    );
  }

  //* Count-only variant using Firestore count() aggregation — avoids downloading full documents just to read .length
  getAttemptedDonationsCountFromServer(): Promise<number> {
    const q = query(collection(getFirestore(), this.env.attemptedDonations))
    return getCountFromServer(q).then(snap => snap.data().count)
  }

  getFailedDonationsCount(): Observable<number> {
    return this.mFirestore.collection(this.env.failedDonations).valueChanges().pipe(
      map(docs => docs.length) // just count documents
    );
  }

  //* Count-only variant using Firestore count() aggregation — avoids downloading full documents just to read .length
  getFailedDonationsCountFromServer(): Promise<number> {
    const q = query(collection(getFirestore(), this.env.failedDonations))
    return getCountFromServer(q).then(snap => snap.data().count)
  }

  getTotalAttemptedAmount(): Observable<number> {
    return this.mFirestore.collection<any>(this.env.donations).valueChanges().pipe(
      map(docs => docs.reduce((sum, d) => sum + Number(d.donation_amount || 0), 0))
    );
  }
  getSuccessDonationCount(): Observable<number> {
    return this.mFirestore.collection(this.env.donations).valueChanges().pipe(
      map(docs => docs.length) // just count documents
    );
  }

  //* Count-only variant using Firestore count() aggregation — avoids downloading full documents just to read .length
  getSuccessDonationCountFromServer(): Promise<number> {
    const q = query(collection(getFirestore(), this.env.donations))
    return getCountFromServer(q).then(snap => snap.data().count)
  }
  getDonationsByMonth() {
    return this.mFirestore.collection<any>(this.env.donations).valueChanges().pipe(
      map(docs => {
        const grouped: { [month: string]: number } = {};
        docs.forEach(d => {
          if (d.createdAt) {
            const date = new Date(d.createdAt);
            const month = date.toLocaleString('default', { month: 'short' }); // Jan, Feb...
            grouped[month] = (grouped[month] || 0) + Number(d.donation_amount || 0);
          }
        });
        return grouped;
      })
    );
  }
  getDonationsByYear(year: number) {
    return this.mFirestore.collection<any>(this.env.donations).valueChanges().pipe(
      map(docs => {
        const grouped: { [month: string]: number } = {};
        docs.forEach(d => {
          if (d.createdAt) {
            const date = new Date(d.createdAt);
            if (date.getFullYear() === year) {
              const month = date.toLocaleString('default', { month: 'short' });
              grouped[month] = (grouped[month] || 0) + Number(d.donation_amount || 0);
            }
          }
        });
        return grouped;
      })
    );
  }
  getCustomCampaignCount(): Observable<number> {
    return this.mFirestore
      .collection<CustomPayment>(this.customPaymentsCollection)
      .valueChanges()
      .pipe(map(docs => docs.length));
  }

  //* Count-only variant using Firestore count() aggregation — avoids downloading full documents just to read .length
  getCustomCampaignCountFromServer(): Promise<number> {
    const q = query(collection(getFirestore(), this.customPaymentsCollection))
    return getCountFromServer(q).then(snap => snap.data().count)
  }

  getActiveCustomPayments(): Observable<CustomPayment[]> {
    return this.mFirestore
      .collection<CustomPayment>(this.customPaymentsCollection, ref =>
        ref.where('disabled', '==', false)
      )
      .snapshotChanges()
      .pipe(
        map(changes => changes.map(c => ({
          id: c.payload.doc.id,
          ...c.payload.doc.data() as CustomPayment
        })))
      );
  }

}
